import { PalmettoAutocomplete, PalmettoIcon } from '@palmetto/dsm-components';
import { useCallback, useMemo, type FC } from 'react';
import { useWatch, type Control, type UseFormSetValue } from 'react-hook-form';

import type { DialogFormValues, FlagMappingFormValue } from './checklistItemInputOptions';
import { buildLevelItems } from './flagMappingOptions';
import { useFlagMappingErrors, useFlagSettings } from './FlagSettingsContext';

export interface FlagMappingRowProps {
  inputIndex: number;
  optionIndex: number;
  mappingIndex: number;
  control: Control<DialogFormValues>;
  setValue: UseFormSetValue<DialogFormValues>;
  remove: (index: number) => void;
}

interface ValueChangeDetails {
  value: string[];
}

interface InputValueChangeDetails {
  inputValue: string;
  reason?: string;
}

const MAX_MENU_HEIGHT = 320;

export const FlagMappingRow: FC<FlagMappingRowProps> = ({
  inputIndex,
  optionIndex,
  mappingIndex,
  control,
  setValue,
  remove,
}) => {
  const settings = useFlagSettings();
  const errors = useFlagMappingErrors();

  const path = `inputs.${inputIndex}.options.${optionIndex}.flagMappings.${mappingIndex}` as const;
  const mapping = useWatch({ control, name: path }) as Partial<FlagMappingFormValue> | undefined;

  const settingId = mapping?.requirementFlagSettingId || undefined;
  const criterionId = mapping?.criterionId;
  const problemId = mapping?.problemId;
  const setting = settings.find((s) => String(s.id) === settingId);
  const criterion = setting?.acceptanceCriteria?.find((c) => String(c.id) === criterionId);

  const settingItems = useMemo(
    () =>
      buildLevelItems(
        settings
          .map((s) => ({ label: s.name, value: String(s.id) }))
          .toSorted((a, b) => a.label.localeCompare(b.label)),
        settingId,
      ),
    [settings, settingId],
  );

  const criterionItems = useMemo(
    () =>
      buildLevelItems(
        (setting?.acceptanceCriteria ?? []).map((c) => ({
          label: c.displayText,
          value: String(c.id),
        })),
        criterionId,
      ),
    [setting, criterionId],
  );

  const problemItems = useMemo(
    () =>
      buildLevelItems(
        (criterion?.problems ?? []).map((p) => ({
          label: p.displayText,
          value: String(p.id),
        })),
        problemId,
      ),
    [criterion, problemId],
  );

  const applySetting = useCallback(
    (next?: string) => {
      if (next === settingId) return;

      setValue(path, { requirementFlagSettingId: next ?? '' });
    },
    [setValue, path, settingId],
  );

  const applyCriterion = useCallback(
    (next?: string) => {
      if (!settingId || next === criterionId) return;

      setValue(path, {
        requirementFlagSettingId: settingId,
        ...(next ? { criterionId: next } : {}),
      });
    },
    [setValue, path, settingId, criterionId],
  );

  const applyProblem = useCallback(
    (next?: string) => {
      if (!settingId || !criterionId || next === problemId) return;

      setValue(path, {
        requirementFlagSettingId: settingId,
        criterionId,
        ...(next ? { problemId: next } : {}),
      });
    },
    [setValue, path, settingId, criterionId, problemId],
  );

  const handleSettingValueChange = useCallback(
    ({ value }: ValueChangeDetails) => applySetting(value[0] || undefined),
    [applySetting],
  );

  const handleCriterionValueChange = useCallback(
    ({ value }: ValueChangeDetails) => applyCriterion(value[0] || undefined),
    [applyCriterion],
  );

  const handleProblemValueChange = useCallback(
    ({ value }: ValueChangeDetails) => applyProblem(value[0] || undefined),
    [applyProblem],
  );

  const handleSettingInputChange = useCallback(
    ({ inputValue, reason }: InputValueChangeDetails) => {
      if (!inputValue && reason === 'input-change') applySetting();
    },
    [applySetting],
  );

  const handleCriterionInputChange = useCallback(
    ({ inputValue, reason }: InputValueChangeDetails) => {
      if (!inputValue && reason === 'input-change') applyCriterion();
    },
    [applyCriterion],
  );

  const handleProblemInputChange = useCallback(
    ({ inputValue, reason }: InputValueChangeDetails) => {
      if (!inputValue && reason === 'input-change') applyProblem();
    },
    [applyProblem],
  );

  const handleRemove = useCallback(() => remove(mappingIndex), [remove, mappingIndex]);

  const requirementError = !settingId ? errors[path] : undefined;

  const requirementValidation = useMemo(
    () => (requirementError ? ({ type: 'error', text: requirementError } as const) : undefined),
    [requirementError],
  );

  return (
    <div className="mb-4 flex items-start gap-8">
      <div className="grid min-w-0 flex-1 grid-cols-1 gap-8 sm:grid-cols-3">
        <div className="min-w-0">
          <PalmettoAutocomplete
            label="Flag Requirement"
            items={settingItems}
            maxMenuHeight={MAX_MENU_HEIGHT}
            onValueChange={handleSettingValueChange}
            onInputValueChange={handleSettingInputChange}
            validationMessage={requirementValidation}
          />
        </div>
        <div className="min-w-0">
          <PalmettoAutocomplete
            key={settingId ?? ''}
            label="Flag"
            items={criterionItems}
            maxMenuHeight={MAX_MENU_HEIGHT}
            disabled={!settingId}
            onValueChange={handleCriterionValueChange}
            onInputValueChange={handleCriterionInputChange}
          />
        </div>
        <div className="min-w-0">
          <PalmettoAutocomplete
            key={`${settingId ?? ''}|${criterionId ?? ''}`}
            label="Issue"
            items={problemItems}
            maxMenuHeight={MAX_MENU_HEIGHT}
            disabled={!criterionId}
            onValueChange={handleProblemValueChange}
            onInputValueChange={handleProblemInputChange}
          />
        </div>
      </div>
      <button
        type="button"
        onClick={handleRemove}
        className="mt-20 cursor-pointer border-none bg-transparent p-0 leading-none text-text-tertiary hover:text-text-primary"
        aria-label="Remove flag mapping"
      >
        <PalmettoIcon name="trash" size="xs" />
      </button>
    </div>
  );
};
